import { auth, db } from '../lib/firebase';
import { 
  collection, 
  doc, 
  getDoc, 
  setDoc, 
  updateDoc, 
  deleteDoc, 
  query, 
  where, 
  getDocs, 
  increment,
  arrayUnion,
  arrayRemove,
  serverTimestamp
} from 'firebase/firestore';

export interface SubscriptionData {
  id?: string;
  subscriberId: string;
  publisherId: string;
  subscribedAt: Date;
  subscriptionTier: 'free' | 'premiumMonthly' | 'premiumYearly';
  isActive: boolean;
}

export interface PublisherStats {
  subscribers: number;
  views: number;
  likes: number;
  shares: number;
  totalContent: number;
}

export interface CreatorBadge {
  id: string;
  name: string;
  tier: 'bronze' | 'silver' | 'gold' | 'platinum';
  color: string;
  benefits: string[];
  requirements: {
    followers: number;
    content: number;
    revenue: number;
  };
}

export interface SubscriptionTier {
  id: string;
  name: string;
  price: number;
  benefits: string[];
  color: string;
  adFree: boolean;
  badge?: CreatorBadge;
  level: number;
  duration: number; // -1 for lifetime, positive number for months
  priceId?: string;
  paymentLink?: string;
}

export interface UserSubscription {
  id: string;
  userId: string;
  tier: SubscriptionTier;
  startDate: Date;
  endDate: Date;
  isActive: boolean;
  status: 'active' | 'expired' | 'cancelled' | 'pending';
}

// Use webhook server URL for cancellation and billing portal
const WEBHOOK_SERVER_URL = process.env.REACT_APP_WEBHOOK_SERVER_URL?.replace(/\/$/, '') || undefined;
if (!WEBHOOK_SERVER_URL) {
  console.error('[SubscriptionService] REACT_APP_WEBHOOK_SERVER_URL is not set. Please check your .env file and restart the dev server.');
}
const PREMIUM_SUBSCRIPTION_COLLECTION = 'premiumSubscriptions';
const STRIPE_PAYMENT_LINKS: Record<string, string | undefined> = {
  premiumMonthly: process.env.REACT_APP_STRIPE_LINK_PREMIUM_MONTHLY || 'https://buy.stripe.com/test_eVqfZhfUs04sbRQa0q7EQ08',
  premiumYearly: process.env.REACT_APP_STRIPE_LINK_PREMIUM_YEARLY || 'https://buy.stripe.com/test_3cI7sLcIg8AY098egG7EQ07',
};
const STRIPE_PRICE_IDS: Record<string, string | undefined> = {
  premiumMonthly: process.env.REACT_APP_STRIPE_PRICE_PREMIUM_MONTHLY,
  premiumYearly: process.env.REACT_APP_STRIPE_PRICE_PREMIUM_YEARLY,
};

const timestampToDate = (value: any): Date | null => {
  if (!value) return null;
  if (typeof value.toDate === 'function') {
    return value.toDate();
  }
  if (typeof value === 'number') {
    return new Date(value);
  }
  return null;
};

// Follow a publisher/creator
export async function followPublisher(publisherId: string, subscriberId: string): Promise<void> {
  try {
    // Get publisher profile data
    const publisherRef = doc(db, 'users', publisherId);
    const publisherDoc = await getDoc(publisherRef);
    const publisherData = publisherDoc.data();
    
    // Write to user's subscriptions subcollection (for real-time updates)
    const userSubscriptionRef = doc(db, 'users', subscriberId, 'subscriptions', publisherId);
    await setDoc(userSubscriptionRef, {
      id: publisherId,
      name: publisherData?.name || publisherData?.username || 'Unknown Creator',
      avatar: publisherData?.avatarUrl || '',
      followers: 0, // Will be updated by stats
      verified: false,
      isOnline: false,
      lastActive: serverTimestamp(),
      newContentCount: 0,
      subscribedAt: serverTimestamp()
    });

    // Also keep top-level subscription for querying
    const subscriptionRef = doc(db, 'subscriptions', `${subscriberId}_${publisherId}`);
    await setDoc(subscriptionRef, {
      subscriberId,
      publisherId,
      subscribedAt: serverTimestamp(),
      subscriptionTier: 'free',
      isActive: true
    });

    // Update publisher's subscriber count
    await updateDoc(publisherRef, {
      subscriberCount: increment(1),
      lastUpdated: serverTimestamp()
    });
  } catch (error) {
    console.error('Error following publisher:', error);
    throw error;
  }
}

// Unfollow a publisher/creator
export async function unfollowPublisher(publisherId: string, subscriberId: string): Promise<void> {
  try {
    // Delete from user's subscriptions subcollection
    const userSubscriptionRef = doc(db, 'users', subscriberId, 'subscriptions', publisherId);
    await deleteDoc(userSubscriptionRef);

    // Delete from top-level subscriptions
    const subscriptionRef = doc(db, 'subscriptions', `${subscriberId}_${publisherId}`);
    await deleteDoc(subscriptionRef);

    // Update publisher's subscriber count
    const publisherRef = doc(db, 'users', publisherId);
    await updateDoc(publisherRef, {
      subscriberCount: increment(-1),
      lastUpdated: serverTimestamp()
    });
  } catch (error) {
    console.error('Error unfollowing publisher:', error);
    throw error;
  }
}

// Check if user is following a publisher
export async function isFollowingPublisher(publisherId: string, subscriberId: string): Promise<boolean> {
  try {
    // Check user's subscriptions subcollection first
    const userSubscriptionRef = doc(db, 'users', subscriberId, 'subscriptions', publisherId);
    const userSubscriptionDoc = await getDoc(userSubscriptionRef);
    
    if (userSubscriptionDoc.exists()) {
      return true;
    }
    
    // Fallback to top-level subscription check
    const subscriptionRef = doc(db, 'subscriptions', `${subscriberId}_${publisherId}`);
    const subscriptionDoc = await getDoc(subscriptionRef);
    
    return subscriptionDoc.exists() && subscriptionDoc.data()?.isActive === true;
  } catch (error) {
    console.error('Error checking follow status:', error);
    return false;
  }
}

// Legacy function names for backward compatibility
export async function subscribeToPublisher(publisherId: string, subscriberId: string): Promise<void> {
  return followPublisher(publisherId, subscriberId);
}

export async function unsubscribeFromPublisher(publisherId: string, subscriberId: string): Promise<void> {
  return unfollowPublisher(publisherId, subscriberId);
}

export async function isSubscribedToPublisher(publisherId: string, subscriberId: string): Promise<boolean> {
  return isFollowingPublisher(publisherId, subscriberId);
}

// Get user's subscriptions
export async function getUserSubscriptions(subscriberId: string): Promise<SubscriptionData[]> {
  try {
    const subscriptionsQuery = query(
      collection(db, 'subscriptions'),
      where('subscriberId', '==', subscriberId),
      where('isActive', '==', true)
    );
    
    const subscriptionsSnapshot = await getDocs(subscriptionsQuery);
    return subscriptionsSnapshot.docs.map(doc => {
      const data = doc.data();
      return {
        id: doc.id,
        subscriberId: data.subscriberId || '',
        publisherId: data.publisherId || '',
        subscribedAt: data.subscribedAt?.toDate() || new Date(),
        subscriptionTier: data.subscriptionTier || 'free',
        isActive: data.isActive || false
      } as SubscriptionData;
    });
  } catch (error) {
    console.error('Error fetching user subscriptions:', error);
    return [];
  }
}

// Get publisher's subscribers
export async function getPublisherSubscribers(publisherId: string): Promise<SubscriptionData[]> {
  try {
    const subscribersQuery = query(
      collection(db, 'subscriptions'),
      where('publisherId', '==', publisherId),
      where('isActive', '==', true)
    );
    
    const subscribersSnapshot = await getDocs(subscribersQuery);
    return subscribersSnapshot.docs.map(doc => {
      const data = doc.data();
      return {
        id: doc.id,
        subscriberId: data.subscriberId || '',
        publisherId: data.publisherId || '',
        subscribedAt: data.subscribedAt?.toDate() || new Date(),
        subscriptionTier: data.subscriptionTier || 'free',
        isActive: data.isActive || false
      } as SubscriptionData;
    });
  } catch (error) {
    console.error('Error fetching publisher subscribers:', error);
    return [];
  }
}

// Get publisher stats (subscribers, views, likes, shares)
export async function getPublisherStats(publisherId: string): Promise<PublisherStats> {
  try {
    // Get subscriber count
    const subscribers = await getPublisherSubscribers(publisherId);
    const subscriberCount = subscribers.length;

    // Get content stats
    const contentQuery = query(
      collection(db, 'content'),
      where('uploaderId', '==', publisherId),
      where('status', '==', 'published')
    );
    
    const contentSnapshot = await getDocs(contentQuery);
    const allContent = contentSnapshot.docs.map(doc => doc.data());

    // Calculate aggregated stats
    const totalViews = allContent.reduce((sum, item) => sum + (item.views || 0), 0);
    const totalLikes = allContent.reduce((sum, item) => sum + (item.likes || 0), 0);
    const totalShares = allContent.reduce((sum, item) => sum + (item.shares || 0), 0);

    return {
      subscribers: subscriberCount,
      views: totalViews,
      likes: totalLikes,
      shares: totalShares,
      totalContent: allContent.length
    };
  } catch (error) {
    console.error('Error fetching publisher stats:', error);
    return {
      subscribers: 0,
      views: 0,
      likes: 0,
      shares: 0,
      totalContent: 0
    };
  }
}

// Like content
export async function likeContent(contentId: string, userId: string): Promise<void> {
  try {
    const contentRef = doc(db, 'content', contentId);
    
    // Add user to likes array and increment like count
    await updateDoc(contentRef, {
      likes: increment(1),
      likedBy: arrayUnion(userId),
      lastUpdated: serverTimestamp()
    });
  } catch (error) {
    console.error('Error liking content:', error);
    throw error;
  }
}

// Unlike content
export async function unlikeContent(contentId: string, userId: string): Promise<void> {
  try {
    const contentRef = doc(db, 'content', contentId);
    
    // Remove user from likes array and decrement like count
    await updateDoc(contentRef, {
      likes: increment(-1),
      likedBy: arrayRemove(userId),
      lastUpdated: serverTimestamp()
    });
  } catch (error) {
    console.error('Error unliking content:', error);
    throw error;
  }
}

// Share content
export async function shareContent(contentId: string, userId: string): Promise<void> {
  try {
    const contentRef = doc(db, 'content', contentId);
    
    // Increment share count
    await updateDoc(contentRef, {
      shares: increment(1),
      lastUpdated: serverTimestamp()
    });
  } catch (error) {
    console.error('Error sharing content:', error);
    throw error;
  }
}

// Check if user has liked content
export async function hasUserLikedContent(contentId: string, userId: string): Promise<boolean> {
  try {
    const contentRef = doc(db, 'content', contentId);
    const contentDoc = await getDoc(contentRef);
    
    if (!contentDoc.exists()) return false;
    
    const likedBy = contentDoc.data()?.likedBy || [];
    return likedBy.includes(userId);
  } catch (error) {
    console.error('Error checking like status:', error);
    return false;
  }
}

// View content (increment view count)
export async function viewContent(contentId: string, userId: string): Promise<void> {
  try {
    const contentRef = doc(db, 'content', contentId);
    
    // Increment view count
    await updateDoc(contentRef, {
      views: increment(1),
      lastUpdated: serverTimestamp()
    });

    // Record unique view (optional - for analytics)
    const viewRef = doc(db, 'contentViews', `${contentId}_${userId}_${Date.now()}`);
    await setDoc(viewRef, {
      contentId,
      userId,
      viewedAt: serverTimestamp()
    });
  } catch (error) {
    console.error('Error recording view:', error);
    throw error;
  }
}

// Get user subscription (single subscription)
export async function getUserSubscription(userId: string): Promise<UserSubscription | null> {
  try {
    const subscriptionRef = doc(db, PREMIUM_SUBSCRIPTION_COLLECTION, userId);
    const subscriptionDoc = await getDoc(subscriptionRef);
    if (!subscriptionDoc.exists()) {
      return null;
    }

    const data = subscriptionDoc.data() as any;
    const tiers = await getAvailableTiers();
    const tier = tiers.find(t => t.id === data.tierId) || tiers.find(t => t.id === 'premiumMonthly');

    if (!tier) {
      return null;
    }

    const startDate = timestampToDate(data.currentPeriodStart) || new Date();
    const endDate = timestampToDate(data.currentPeriodEnd) || new Date();

    return {
      id: data.stripeSubscriptionId || subscriptionDoc.id,
      userId,
      tier,
      startDate,
      endDate,
      isActive: data.status === 'active' && !data.cancelAtPeriodEnd,
      status: data.status || 'pending'
    };
  } catch (error) {
    console.error('Error fetching user subscription:', error);
    return null;
  }
}

// Redeem crowdfunding code
export async function redeemCrowdfundingCode(code: string, userId: string): Promise<UserSubscription | null> {
  try {
    // Mock implementation - replace with actual crowdfunding logic
    // For now, return null as this is a placeholder
    return null;
  } catch (error) {
    console.error('Error redeeming crowdfunding code:', error);
    throw error;
  }
}

// Check if user has ad-free status
export async function isUserAdFree(userId: string): Promise<boolean> {
  try {
    const subscription = await getUserSubscription(userId);
    return Boolean(subscription?.isActive && subscription?.tier.adFree);
  } catch (error) {
    console.error('Error checking ad-free status:', error);
    return false;
  }
}

// Get available subscription tiers
export async function getAvailableTiers(): Promise<SubscriptionTier[]> {
  try {
    const tiers: SubscriptionTier[] = [
      {
        id: 'free',
        name: 'Free',
        price: 0,
        benefits: ['Basic content access', 'Limited features'],
        color: 'gray',
        adFree: false,
        level: 1,
        duration: -1,
        priceId: undefined,
        paymentLink: undefined,
      }
    ];

    if (STRIPE_PAYMENT_LINKS.premiumMonthly && STRIPE_PRICE_IDS.premiumMonthly) {
      tiers.push({
        id: 'premiumMonthly',
        name: 'Premium Monthly',
        price: 4.99,
        benefits: ['Ad-free experience', 'Premium content', 'Early access'],
        color: 'blue',
        adFree: true,
        level: 2,
        duration: 1,
        priceId: STRIPE_PRICE_IDS.premiumMonthly,
        paymentLink: STRIPE_PAYMENT_LINKS.premiumMonthly,
      });
    }

    if (STRIPE_PAYMENT_LINKS.premiumYearly && STRIPE_PRICE_IDS.premiumYearly) {
      tiers.push({
        id: 'premiumYearly',
        name: 'Premium Yearly',
        price: 55,
        benefits: ['Ad-free experience', 'Premium content', 'Early access', 'Best annual value'],
        color: 'purple',
        adFree: true,
        level: 3,
        duration: 12,
        priceId: STRIPE_PRICE_IDS.premiumYearly,
        paymentLink: STRIPE_PAYMENT_LINKS.premiumYearly,
      });
    }

    return tiers;
  } catch (error) {
    console.error('Error fetching available tiers:', error);
    return [];
  }
}

export async function startPremiumCheckout(tierId: string): Promise<void> {
  const currentUser = auth.currentUser;
  if (!currentUser) {
    throw new Error('You must be signed in to subscribe.');
  }

  const paymentLink = STRIPE_PAYMENT_LINKS[tierId];
  const priceId = STRIPE_PRICE_IDS[tierId];
  if (!paymentLink || !priceId) {
    throw new Error('This subscription tier is not currently available.');
  }

  const url = new URL(paymentLink);
  if (currentUser.email) {
    url.searchParams.set('prefilled_email', currentUser.email);
  }
  url.searchParams.set('client_reference_id', currentUser.uid);
  url.searchParams.set('price_id', priceId);

  window.location.href = url.toString();
}

export async function openBillingPortal(): Promise<void> {
  if (!WEBHOOK_SERVER_URL) {
    throw new Error('Webhook server URL is not configured. Please set REACT_APP_WEBHOOK_SERVER_URL in your .env file and restart the dev server.');
  }

  const currentUser = auth.currentUser;
  if (!currentUser) {
    throw new Error('You must be signed in to manage your subscription.');
  }

  const idToken = await currentUser.getIdToken();
  const response = await fetch(`${WEBHOOK_SERVER_URL}/createBillingPortalSession`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${idToken}`,
    },
    body: JSON.stringify({
      returnUrl: `${window.location.origin}/account/subscription`,
    }),
  });

  if (!response.ok) {
    const errorBody = await response.json().catch(() => ({}));
    throw new Error(errorBody?.error || 'Unable to open billing portal');
  }

  const { url } = await response.json();
  if (url) {
    window.open(url, '_blank', 'noopener,noreferrer');
  } else {
    throw new Error('Billing portal URL not available');
  }
}

// Default export for backward compatibility
export default {
  followPublisher,
  unfollowPublisher,
  isFollowingPublisher,
  subscribeToPublisher, // Legacy
  unsubscribeFromPublisher, // Legacy
  isSubscribedToPublisher, // Legacy
  getUserSubscriptions,
  getUserSubscription,
  getPublisherSubscribers,
  getPublisherStats,
  likeContent,
  unlikeContent,
  shareContent,
  hasUserLikedContent,
  viewContent,
  redeemCrowdfundingCode,
  isUserAdFree,
  getAvailableTiers,
  startPremiumCheckout,
  openBillingPortal
};